import { useMemo } from 'react';

import type {
  CompanyMetrics,
  TrendDirection,
} from './useCompetitorComposition';
import type { CompanyPostings } from './usePostingsComparison';
import type { CompanySentiment } from './useSentimentComparison';

export type RiskLevel = 'low' | 'medium' | 'high';

export interface EmployerHealth {
  companyId: number;
  shortName: string;
  stabilityScore: number; // 0–100
  risk: RiskLevel;
  hiringVelocity: TrendDirection;
  growthPotential: number; // projected annual growth as decimal (e.g. 0.05 = 5%)
  summaryText: string;
}

const clamp = (val: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, val));

const trendToScore = (trend: TrendDirection): number => {
  if (trend === 'growing') return 1;
  if (trend === 'stable') return 0.5;
  return 0;
};

export const computeStability = (
  metrics: CompanyMetrics | undefined,
  sentiment: CompanySentiment | undefined,
  postings: CompanyPostings | undefined
): { score: number; risk: RiskLevel } => {
  let score = 50; // baseline

  if (metrics) {
    // Tenure: higher is more stable (normalize around 36 months as "good")
    if (metrics.tenure !== null) {
      score += clamp(((metrics.tenure - 24) / 24) * 15, -15, 15);
    }

    // Attrition: lower is better (invert)
    if (metrics.attritionRate !== null) {
      score -= clamp(metrics.attritionRate * 100, 0, 20);
    }

    // Growth trend
    score += trendToScore(metrics.growthTrend) * 10;

    // Hiring trend (positive signal if growing)
    score += trendToScore(metrics.hiringTrend) * 5;
  }

  if (
    sentiment?.scores.overall !== null &&
    sentiment?.scores.overall !== undefined
  ) {
    // Sentiment: scores are on 0–1 scale (0.5 = neutral). Contribute ±15 points.
    score += clamp((sentiment.scores.overall - 0.5) * 30, -15, 15);
  }

  if (
    postings?.activePostings !== null &&
    postings?.activePostings !== undefined
  ) {
    // Active postings as a sign of activity (positive but diminishing)
    score += clamp(Math.log2(postings.activePostings + 1) * 2, 0, 10);
  }

  const finalScore = clamp(Math.round(score), 0, 100);

  let risk: RiskLevel = 'medium';
  if (finalScore >= 65) risk = 'low';
  else if (finalScore < 40) risk = 'high';

  return { score: finalScore, risk };
};

export const computeGrowthPotential = (
  metrics: CompanyMetrics | undefined
): number => {
  if (!metrics) return 0;

  const growth = metrics.growthRate ?? 0;
  const hiring = metrics.hiringRate ?? 0;
  const attrition = metrics.attritionRate ?? 0;

  // Simple model: net growth + hiring momentum
  return growth + (hiring - attrition) * 0.5;
};

export const generateSummary = (
  shortName: string,
  stability: { score: number; risk: RiskLevel },
  hiringVelocity: TrendDirection,
  growthPotential: number
): string => {
  let stabilityWord = 'moderate stability';
  if (stability.risk === 'low') stabilityWord = 'strong stability';
  else if (stability.risk === 'high') stabilityWord = 'elevated risk signals';

  let hiringWord = 'steady hiring';
  if (hiringVelocity === 'growing') hiringWord = 'active hiring';
  else if (hiringVelocity === 'declining') hiringWord = 'reduced hiring';

  let growthWord = 'stable workforce size';
  if (growthPotential > 0.03) growthWord = 'positive growth trajectory';
  else if (growthPotential < -0.02) growthWord = 'contracting workforce';

  return `${shortName} shows ${stabilityWord} with ${hiringWord} and ${growthWord}.`;
};

interface UseEmployerHealthResult {
  healthData: EmployerHealth[];
  isLoading: boolean;
}

export const useEmployerHealth = (
  metrics: CompanyMetrics[],
  sentiments: CompanySentiment[],
  postings: CompanyPostings[],
  isDataLoading: boolean
): UseEmployerHealthResult => {
  const healthData = useMemo((): EmployerHealth[] => {
    if (metrics.length === 0) return [];

    const sentimentMap = new Map(sentiments.map((s) => [s.companyId, s]));
    const postingsMap = new Map(postings.map((p) => [p.companyId, p]));

    return metrics.map((m) => {
      const sentiment = sentimentMap.get(m.companyId);
      const posting = postingsMap.get(m.companyId);

      const stability = computeStability(m, sentiment, posting);
      const hiringVelocity = m.hiringTrend;
      const growthPotential = computeGrowthPotential(m);

      return {
        companyId: m.companyId,
        shortName: m.shortName,
        stabilityScore: stability.score,
        risk: stability.risk,
        hiringVelocity,
        growthPotential,
        summaryText: generateSummary(
          m.shortName,
          stability,
          hiringVelocity,
          growthPotential
        ),
      };
    });
  }, [metrics, sentiments, postings]);

  return { healthData, isLoading: isDataLoading };
};
